<template>
    <section
        v-if="!(breakpoints?.isMobile && slice.primary.hideInMobile)"
        :id="slice.primary.anchorName || undefined"
        ref="section"
        :class="['slice slice-cta slice-cta--default', {'slice-cta--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="call-to-action"
        data-testid="slice-cta-default"
    >
        <div class="slice-content">
            <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" data-testid="slice-cta-title" />
            <PrismicRichText
                v-if="slice.primary.description"
                :field="slice.primary.description"
                class="slice-description"
                data-testid="slice-cta-description"
            />
            <div v-if="cta" class="slice-cta__buttons">
                <VJoyButton :href="$withLocalePrefix(cta.url)" :variant="cta.variant" data-component-source="cta" data-testid="slice-cta-button">
                    {{ cta.text }}
                </VJoyButton>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {CtaSlice} from '@prismicio-types';
    import {useBreakpoints, useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';

    type CtaDefaultVariation = Extract<CtaSlice, {variation: 'default'}>;

    const props = defineProps(getSliceComponentProps<CtaDefaultVariation>());

    const breakpoints = useBreakpoints();
    const section = useTemplateRef('section');

    type CtaWithVariant = {url: string; text: string; variant: 'primary' | 'secondary' | undefined};

    const cta = computed<CtaWithVariant | null>(() => {
        const link = props.slice.primary.cta;
        if (!isFilled.link(link)) {
            return null;
        }

        const filledLink = link as {url: string; text?: string; variant?: 'primary' | 'secondary'};
        return {
            url: filledLink.url,
            text: filledLink.text ?? '',
            variant: filledLink.variant ?? undefined,
        };
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-cta {
        &--default {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            padding: 100px 20px 90px;

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 70px;
                width: 100%;
                height: 100%;
                background: url('../../../../assets/svg/cta-shapes.svg') no-repeat top left;
                background-size: 50%;
                z-index: -1;

                @include mq.screen_xs(min) {
                    top: 40px;
                }

                @include mq.screen_sm(min) {
                    top: 20px;
                    background-size: auto;
                }
            }

            &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 42%;
                height: 60%;
                width: 115%;
                background: var(--joy-color-primary-50);
                border-radius: 0 220px 220px 0;
                transform: translate(-50%, -50%) rotate(-4deg);
                z-index: -1;

                @include mq.screen_sm(min) {
                    height: 80%;
                }

                @include mq.screen_lg(min) {
                    width: 90%;
                }
            }

            .slice-cta__buttons {
                width: initial;

                .joy-button {
                    background-color: transparent;
                    border-color: var(--joy-color-neutral-10);
                    font-size: var(--joy-font-size-primary-600);
                    padding-left: var(--joy-core-spacing-5);
                    padding-right: var(--joy-core-spacing-5);

                    &:hover {
                        background-color: var(--joy-color-primary-70);
                    }

                    @include mq.screen_sm(min) {
                        padding: var(--joy-core-spacing-5) var(--joy-core-spacing-9);
                    }
                }
            }

            .slice-title {
                font-family: var(--joy-font-family-title);
                font-weight: var(--joy-font-weight-normal);
                line-height: var(--joy-line-height-high);
                font-size: 40px;
                color: var(--joy-color-neutral-0);

                h1,
                h2 {
                    font-size: inherit;
                    color: var(--joy-color-neutral-0);
                    font-weight: inherit;
                    margin-bottom: var(--joy-core-spacing-4);

                    @include mq.screen_lg(min) {
                        margin-bottom: var(--joy-core-spacing-6);
                    }
                }

                @include mq.screen_lg(min) {
                    font-size: var(--joy-font-size-secondary-800);
                }
            }

            .slice-description {
                color: var(--joy-color-neutral-0);
                margin-bottom: var(--joy-core-spacing-4);

                @include mq.screen_lg(min) {
                    margin-bottom: var(--joy-core-spacing-6);
                }
            }
        }
    }
</style>
